import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import platformApiService, { MunicipalitySummary } from '../../services/platformApi';
import {
  MagnifyingGlassIcon,
  UsersIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  ArrowTopRightOnSquareIcon,
  PauseCircleIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';

const STATUS_STYLES: Record<string, string> = {
  active: 'bg-emerald-500/10 text-emerald-300 ring-1 ring-emerald-500/30',
  trial: 'bg-sky-500/10 text-sky-300 ring-1 ring-sky-500/30',
  suspended: 'bg-red-500/10 text-red-300 ring-1 ring-red-500/30',
  cancelled: 'bg-slate-500/10 text-slate-400 ring-1 ring-slate-500/30',
};

const fmtDate = (value: string | null) => (value ? new Date(value).toLocaleDateString() : '—');

const StatCard: React.FC<{ label: string; value: string | number; icon: React.ComponentType<React.SVGProps<SVGSVGElement>> }> = ({ label, value, icon: Icon }) => (
  <div className="flex items-center gap-3 rounded-xl border border-slate-800 bg-slate-900 px-4 py-3">
    <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-800">
      <Icon className="h-4.5 w-4.5 text-slate-400" />
    </div>
    <div>
      <div className="text-lg font-semibold text-white">{value}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  </div>
);

const PlatformDashboard: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [statusModal, setStatusModal] = useState<{ municipality: MunicipalitySummary; action: 'suspend' | 'activate' } | null>(null);
  const [reason, setReason] = useState('');
  const [impersonating, setImpersonating] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['platform-municipalities'],
    queryFn: async () => {
      const response = await platformApiService.listMunicipalities();
      if (!response.success) throw new Error(response.error?.message || 'Failed to load municipalities');
      return response.data || [];
    },
  });

  const statusMutation = useMutation({
    mutationFn: async ({ id, status, reason: r }: { id: string; status: string; reason?: string }) => {
      const response = await platformApiService.setMunicipalityStatus(id, status, r);
      if (!response.success) throw new Error(response.error?.message || 'Failed to update status');
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['platform-municipalities'] });
      setStatusModal(null);
      setReason('');
    },
  });

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  const municipalities = useMemo(() => {
    const list = data || [];
    if (!search.trim()) return list;
    const q = search.trim().toLowerCase();
    return list.filter((m) => m.name.toLowerCase().includes(q) || m.code.toLowerCase().includes(q));
  }, [data, search]);

  const totals = useMemo(() => {
    const list = data || [];
    return {
      count: list.length,
      active: list.filter((m) => m.status === 'active').length,
      suspended: list.filter((m) => m.status === 'suspended').length,
      breaches: list.reduce((sum, m) => sum + m.slaBreaches, 0),
    };
  }, [data]);

  const handleImpersonate = async (m: MunicipalitySummary) => {
    setImpersonating(m.id);
    try {
      const response = await platformApiService.impersonate(m.id);
      if (!response.success || !response.data) {
        throw new Error(response.error?.message || 'Impersonation failed');
      }
      localStorage.setItem('authToken', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('impersonation', JSON.stringify({ municipalityName: m.name }));
      window.location.href = '/live-dashboard';
    } catch (err) {
      setToast(err instanceof Error ? err.message : 'Impersonation failed');
    } finally {
      setImpersonating(null);
    }
  };

  return (
    <div>
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-white">Municipalities</h1>
          <p className="text-sm text-slate-500">Every tenant on the platform, at a glance.</p>
        </div>
        <div className="relative">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search municipalities…"
            className="w-64 rounded-lg border border-slate-700 bg-slate-900 py-2 pl-9 pr-3 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
          />
        </div>
      </div>

      <div className="mb-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <StatCard label="Total tenants" value={totals.count} icon={UsersIcon} />
        <StatCard label="Active" value={totals.active} icon={PlayCircleIcon} />
        <StatCard label="Suspended" value={totals.suspended} icon={PauseCircleIcon} />
        <StatCard label="SLA breaches (all)" value={totals.breaches} icon={ExclamationTriangleIcon} />
      </div>

      {toast && (
        <div className="mb-4 rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-300">{toast}</div>
      )}

      <div className="overflow-hidden rounded-xl border border-slate-800 bg-slate-900">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-slate-500">Loading municipalities…</div>
        ) : error ? (
          <div className="p-8 text-center text-sm text-red-300">{(error as Error).message}</div>
        ) : municipalities.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-500">No municipalities match your search.</div>
        ) : (
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-slate-800 text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-medium">Municipality</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Staff / Citizens</th>
                <th className="px-4 py-3 font-medium">Reports</th>
                <th className="px-4 py-3 font-medium">Resolution</th>
                <th className="px-4 py-3 font-medium">SLA breaches</th>
                <th className="px-4 py-3 font-medium">Last active</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {municipalities.map((m) => (
                <tr key={m.id} className="border-b border-slate-800/60 last:border-0 hover:bg-slate-800/40">
                  <td className="px-4 py-3">
                    <button className="text-left font-medium text-white hover:text-emerald-300" onClick={() => navigate(`/platform-ops/municipalities/${m.id}`)}>
                      {m.name}
                    </button>
                    <div className="text-xs text-slate-500">{m.code}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium capitalize ${STATUS_STYLES[m.status] || STATUS_STYLES.cancelled}`}>{m.status}</span>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{m.staffCount} / {m.citizenCount}</td>
                  <td className="px-4 py-3 text-slate-300">{m.reportCount} <span className="text-slate-500">({m.openReportCount} open)</span></td>
                  <td className="px-4 py-3 text-slate-300">{m.resolutionRate === null ? '—' : `${m.resolutionRate}%`}</td>
                  <td className="px-4 py-3">
                    {m.slaBreaches > 0 ? (
                      <span className="font-medium text-amber-300">{m.slaBreaches}</span>
                    ) : (
                      <span className="text-slate-500">0</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400">{fmtDate(m.lastActiveAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => handleImpersonate(m)}
                        disabled={impersonating === m.id}
                        title="Sign in as this municipality's admin"
                        className="flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-slate-300 hover:border-emerald-500 hover:text-emerald-300 disabled:opacity-50"
                      >
                        <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5" />
                        {impersonating === m.id ? 'Opening…' : 'Impersonate'}
                      </button>
                      {m.status === 'suspended' ? (
                        <button
                          onClick={() => setStatusModal({ municipality: m, action: 'activate' })}
                          className="flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-emerald-300 hover:border-emerald-500"
                        >
                          <PlayCircleIcon className="h-3.5 w-3.5" />
                          Reactivate
                        </button>
                      ) : (
                        <button
                          onClick={() => setStatusModal({ municipality: m, action: 'suspend' })}
                          className="flex items-center gap-1 rounded-md border border-slate-700 px-2 py-1 text-xs text-red-300 hover:border-red-500"
                        >
                          <PauseCircleIcon className="h-3.5 w-3.5" />
                          Suspend
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {statusModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-sm rounded-xl border border-slate-800 bg-slate-900 p-5 shadow-xl">
            <h3 className="text-sm font-semibold text-white">
              {statusModal.action === 'suspend' ? 'Suspend' : 'Reactivate'} {statusModal.municipality.name}?
            </h3>
            <p className="mt-1 text-xs text-slate-500">
              {statusModal.action === 'suspend'
                ? 'Staff and residents will be unable to sign in until this municipality is reactivated.'
                : 'This municipality will regain full access immediately.'}
            </p>
            {statusModal.action === 'suspend' && (
              <textarea
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Reason (optional, visible in audit log)"
                rows={3}
                className="mt-3 w-full rounded-lg border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-white placeholder-slate-500 focus:border-emerald-500 focus:outline-none focus:ring-1 focus:ring-emerald-500"
              />
            )}
            {statusMutation.isError && (
              <div className="mt-2 text-xs text-red-300">{(statusMutation.error as Error).message}</div>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => { setStatusModal(null); setReason(''); }}
                className="rounded-md px-3 py-1.5 text-sm text-slate-400 hover:text-slate-200"
              >
                Cancel
              </button>
              <button
                onClick={() =>
                  statusMutation.mutate({
                    id: statusModal.municipality.id,
                    status: statusModal.action === 'suspend' ? 'suspended' : 'active',
                    reason: statusModal.action === 'suspend' ? reason : undefined,
                  })
                }
                disabled={statusMutation.isPending}
                className={`rounded-md px-3 py-1.5 text-sm font-semibold disabled:opacity-60 ${
                  statusModal.action === 'suspend' ? 'bg-red-500 text-white hover:bg-red-400' : 'bg-emerald-500 text-slate-950 hover:bg-emerald-400'
                }`}
              >
                {statusMutation.isPending ? 'Working…' : statusModal.action === 'suspend' ? 'Suspend' : 'Reactivate'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlatformDashboard;
